/**
 * Custom Login Page - MFA Only
 * Handles OIDC authentication flow with MFA authentication
 */
'use client'

import { useSearchParams } from 'next/navigation'
import React, { useState, useEffect, Suspense } from 'react'

import { challengeMFA, verifyMFA } from './actions'
import { Header, IdentifierForm, VerificationForm, VerificationSuccess } from '../components'
import { isEmailAddress } from '../utils/validation'

// Authentication modes
type AuthMode = 'email_otp' | 'sms_otp'

// MFA session data
interface MFASession {
  session: string
  challengeName: string
  username: string
  authFlow: 'EMAIL_OTP' | 'SMS_OTP'
}

function LoginContent (): React.JSX.Element {
  // Common state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  // MFA auth state
  const [identifier, setIdentifier] = useState<string>('') // email or phone
  const [challengeCode, setChallengeCode] = useState<string>('')
  const [mfaSession, setMfaSession] = useState<MFASession | null>(null)
  const [challengeSent, setChallengeSent] = useState<boolean>(false)
  const [emailOptIn, setEmailOptIn] = useState<boolean>(false)
  const [verificationSuccess, setVerificationSuccess] = useState<boolean>(false)
  const [redirectUrl, setRedirectUrl] = useState<string>('')

  const searchParams = useSearchParams()

  // Extract OAuth parameters
  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const state = searchParams.get('state')

  // Derive authMode from current identifier - defaults to email_otp
  const authMode: AuthMode = isEmailAddress(identifier) ? 'email_otp' : 'sms_otp'

  useEffect(() => {
    // Validate required OAuth parameters
    if (!clientId || !redirectUri) {
      setError('Missing required OAuth parameters')
    }
  }, [clientId, redirectUri])

  const handleMFAChallenge = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const challengeType = authMode === 'email_otp' ? 'EMAIL_OTP' : 'SMS_OTP'

      const result = await challengeMFA(identifier, challengeType, emailOptIn)

      if (result.success) {
        setMfaSession({
          session: result.session!,
          challengeName: result.challengeName!,
          username: identifier,
          authFlow: challengeType,
        })
        setChallengeSent(true)
      } else {
        setError(result.error || 'Failed to send MFA challenge')
      }
    } catch (err) {
      setError('Failed to send MFA challenge. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleMFAVerify = async (e?: React.FormEvent<HTMLFormElement>, codeToVerify?: string): Promise<void> => {
    e?.preventDefault()
    if (!mfaSession) return

    const codeValue = codeToVerify || challengeCode
    if (!codeValue) {
      setError('Please enter the verification code')
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await verifyMFA(
        mfaSession.username,
        codeValue,
        mfaSession.session,
        mfaSession.challengeName
      )

      if (result.success) {
        setVerificationSuccess(true)

        // Use the secure authorization code from server action
        if (redirectUri) {
          const callbackUrl = new URL(redirectUri)

          // Use the opaque authorization code from server (tokens never exposed to client)
          callbackUrl.searchParams.set('code', result.authorizationCode!)

          // Include the original state parameter that NextAuth.js sent
          if (state) {
            callbackUrl.searchParams.set('state', state)
          }

          window.location.href = callbackUrl.toString()
        }
      } else {
        setError(result.error || 'MFA verification failed')
      }
    } catch (err) {
      setError('MFA verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOTPComplete = (completedCode: string): void => {
    // Auto-submit when all 8 digits are entered
    setChallengeCode(completedCode)
    // Pass the completed code directly to avoid race condition
    handleMFAVerify(undefined, completedCode)
  }

  const resetMFAFlow = (): void => {
    setChallengeSent(false)
    setMfaSession(null)
    setChallengeCode('')
    setError('')
    setRedirectUrl('')
    setIdentifier('')
  }

  if (!clientId || !redirectUri) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">Invalid Request</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Missing required OAuth parameters (client_id, redirect_uri)
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center px-4">
      <Header resetMFAFlow={resetMFAFlow} showBackButton={Boolean(challengeSent && mfaSession)} />

      <div className="max-w-[392px] w-full space-y-4 px-7 pt-7 py-9 mt-6 rounded-2xl shadow-md border border-neutrals-100 bg-white">
        {/* MFA Authentication Form - Challenge */}
        {!challengeSent && (
          <IdentifierForm
            authMode={authMode}
            handleMFAChallenge={handleMFAChallenge}
            identifier={identifier}
            setIdentifier={setIdentifier}
            loading={loading}
            error={error}
            emailOptIn={emailOptIn}
            setEmailOptIn={setEmailOptIn}
            setError={setError}
          />
        )}

        {/* MFA Authentication Form - Verify */}
        {challengeSent && mfaSession && !verificationSuccess && (
          <VerificationForm
            authMode={authMode}
            username={mfaSession?.username}
            challengeCode={challengeCode}
            setChallengeCode={setChallengeCode}
            handleMFAVerify={handleMFAVerify}
            handleMFAChallenge={handleMFAChallenge}
            handleOTPComplete={handleOTPComplete}
            loading={loading}
            error={error}
            setError={setError}
            resetMFAFlow={resetMFAFlow}
          />
        )}

        {/* MFA Authentication Form - Success */}
        {verificationSuccess && (
          <VerificationSuccess redirectUrl={redirectUrl} />
        )}
      </div>
    </div>
  )
}

export default function LoginPage (): React.JSX.Element {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    }
    >
      <LoginContent />
    </Suspense>
  )
}
